export type TextRun = { text: string; match: boolean }

/**
 * Lower-cases one character at a time, keeping any character whose lower-case
 * form is a different length (the dotted capital I becomes two code units).
 * Lower-casing the whole string would shift every index after such a
 * character, so a match found in the folded text would be sliced from the
 * wrong place in the original.
 */
function foldCase(text: string): string {
    let folded = ''
    for (const character of text) {
        const lower = character.toLowerCase()
        folded += lower.length === character.length ? lower : character
    }
    return folded
}

/**
 * Splits text into runs for rendering, marking the ones that match the
 * search term. Case-insensitive, like the LIKE filter it reflects on the
 * server, so what lights up is what matched. Accent folding is not mirrored:
 * the server's collation matches "cafe" to "café" and this will not.
 */
export function highlightMatches(text: string, query: string | undefined): TextRun[] {
    const needle = foldCase((query ?? '').trim())
    if (!needle || !text) {
        return [{ text, match: false }]
    }
    const haystack = foldCase(text)
    const runs: TextRun[] = []
    let cursor = 0
    let index = haystack.indexOf(needle, cursor)
    while (index !== -1) {
        if (index > cursor) {
            runs.push({ text: text.slice(cursor, index), match: false })
        }
        runs.push({ text: text.slice(index, index + needle.length), match: true })
        cursor = index + needle.length
        index = haystack.indexOf(needle, cursor)
    }
    if (cursor < text.length) {
        runs.push({ text: text.slice(cursor), match: false })
    }
    return runs
}
